'use client';

import { useTranslation } from '@/app/_shared/hooks/useTranslation';
import { Button } from '@/components/common/Button';
import {
	DeviceMessageIcon,
	FirstlineIcon,
	GlobalEditIcon,
	LockIcon,
	ShieldTickIcon,
	UserIcon,
} from '@/components/common/Icons';
import { useAuthContext } from '@/contexts/auth';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import BottomSkeleton from './BottomSkeleton';
import ThemeSwitchItem from './ThemeSwitchItem';

type StandardMenuItem = {
	id: string;
	label: string;
	icon: React.ReactNode;
	path: string;
};

type CustomMenuItem = {
	id: string;
	label: string;
	component: React.ReactNode;
};

type MenuItem = StandardMenuItem | CustomMenuItem;

function isCustomMenuItem(item: MenuItem): item is CustomMenuItem {
	return 'component' in item;
}

type MenuGroup = {
	group: string;
	id: string;
	children: MenuItem[];
};

export const Menu = () => {
	const router = useRouter();
	const { isGuest, logout } = useAuthContext();
	const [activeMenuItem, setActiveMenuItem] = useState('');
	const pathname = usePathname();
	const { t } = useTranslation();

	const menuItems: MenuGroup[] = [
		{
			group: t('common:yourProfile'),
			id: 'your-profile',
			children: [
				{
					id: 'profile-centre',
					label: 'Profile centre',
					icon: <UserIcon />,
					path: '/my_profile/centre',
				},
				{
					id: 'change-password',
					label: 'Change password',
					icon: <LockIcon />,
					path: '/my_profile/change_password',
				},
			],
		},
		{
			group: 'Theme & content',
			id: 'theme-content',
			children: [
				{
					id: 'theme-switch',
					label: 'Theme',
					component: <ThemeSwitchItem />,
				},
				{
					id: 'select-categories',
					label: t('common:selectCategories'),
					icon: <FirstlineIcon />,
					path: '/my_profile/select_categories',
				},
				{
					id: 'select-locations',
					label: t('common:selectLocation'),
					icon: <GlobalEditIcon />,
					path: '/my_profile/select_locations',
				},
			],
		},
		{
			group: t('common:helpSupport'),
			id: 'help-support',
			children: [
				{
					id: 'help-centre',
					label: t('common:helpCentre'),
					icon: <DeviceMessageIcon />,
					path: '/my_profile/help_centre',
				},
				{
					id: 'privacy',
					label: t('common:privacy'),
					icon: <ShieldTickIcon />,
					path: '/my_profile/privacy',
				},
			],
		},
	];

	const filteredItems = isGuest
		? menuItems.filter((group) => group.id !== 'your-profile')
		: menuItems;

	useEffect(() => {
		pathname && setActiveMenuItem(pathname);
	}, [pathname]);

	const handleMenuClick = (itemPath: string) => {
		setActiveMenuItem(itemPath);
	};

	const handleClick = () => {
		if (!isGuest) {
			logout();
			return;
		}
		router.push('/my_profile/login');
	};

	return (
		<Suspense fallback={<BottomSkeleton />}>
			<div className="flex flex-col gap-y-[0.9375rem]">
				{filteredItems.map((group) => (
					<div key={group.id} className="flex flex-col gap-y-[0.9375rem]">
						<div className="pl-2.5 text-sm font-bold text-grey-1">
							{group.group}
						</div>
						<ul className="flex flex-col gap-y-2.5">
							{group.children.map((item) => (
								<li key={item.id}>
									{isCustomMenuItem(item) ? (
										item.component
									) : (
										<Link
											href={item.path || '#'}
											className={`p-2.5 flex items-center gap-x-3 cursor-pointer ${activeMenuItem === item.path ? 'bg-grey-4 rounded-[0.3125rem]' : ''}`}
											onClick={() => handleMenuClick(item.path)}
										>
											{item.icon}
											<div className="text-md font-bold text-secondary-black">
												{item.label}
											</div>
										</Link>
									)}
								</li>
							))}
						</ul>
					</div>
				))}
				<Button
					variant="link"
					className="mx-2.5 text-md font-bold w-fit"
					onClick={handleClick}
				>
					{!isGuest && t('common:logout')}
				</Button>
			</div>
		</Suspense>
	);
};
